import { Form } from 'antd';
import { FormProviderProps } from 'antd/es/form/context';
import { Dayjs } from 'dayjs';
import { PropsWithChildren, createContext, useContext, useState } from 'react';

import {
  getDefaultTableParams,
  useTableParams,
} from 'business/providers/table-params';
import { DEFAULT_PAGE_SIZE } from 'business/quality-control/constants';
import { DefectReportStatusSearchable } from 'business/quality-control/pages/defect-report-history/hooks/data/types';

import { ORDER_FIELD, ORDER_TYPE } from '..';

const FILTERS_FORM_NAME = 'defect-report-history-filters-form';

const DefectRange = {
  day: 'day',
  week: 'week',
  month: 'month',
  interval: 'interval',
} as const;

const DefectReportHistoryDate = {
  reportDate: 'reportDate',
  repairDate: 'repairDate',
} as const;

export type DefectReportHistoryFilters = {
  defectType: string | null;
  toRepair: boolean | null;
  indicator: string | null;
  defectReportHistoryDate:
    | (typeof DefectReportHistoryDate)[keyof typeof DefectReportHistoryDate]
    | null;
  defectReportHistoryDateRange: [Dayjs | null, Dayjs | null];
  status: DefectReportStatusSearchable[];
  defectRange: (typeof DefectRange)[keyof typeof DefectRange] | null;
  ringNumberRange: [number | null, number | null];
};

const defaultFilters = {
  defectType: null,
  toRepair: null,
  indicator: null,
  defectReportHistoryDate: null,
  defectReportHistoryDateRange: [null, null],
  status: [],
  defectRange: null,
  ringNumberRange: [null, null],
} as const satisfies DefectReportHistoryFilters;

const DefectReportHistoryFiltersContext =
  createContext<DefectReportHistoryFilters>(defaultFilters);

const DefectReportHistoryResetFiltersContext = createContext<() => void>(
  () => {},
);

const DefectReportHistoryFiltersProvider = ({
  initialFilters,
  children,
}: PropsWithChildren<{
  initialFilters: DefectReportHistoryFilters;
}>) => {
  const [filters, setFilters] =
    useState<DefectReportHistoryFilters>(initialFilters);
  const { setTableParams } = useTableParams();

  const resetTableParams = () => {
    const defaultTableParams = getDefaultTableParams({
      defaultPageSize: DEFAULT_PAGE_SIZE,
      defaultSortField: ORDER_FIELD,
      defaultSortOrder: ORDER_TYPE,
    });
    setTableParams(defaultTableParams);
  };

  const onFormChange: FormProviderProps['onFormChange'] = (
    formName,
    { forms },
  ) => {
    if (formName === FILTERS_FORM_NAME) {
      const form = forms[formName];

      const values = {
        ...defaultFilters,
        ...form.getFieldsValue(),
      } satisfies DefectReportHistoryFilters;

      setFilters(values);
      form.setFieldsValue(values);

      // Reset pagination, order, etc.
      resetTableParams();
    }
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    resetTableParams();
  };

  return (
    <DefectReportHistoryFiltersContext.Provider value={filters}>
      <DefectReportHistoryResetFiltersContext.Provider value={resetFilters}>
        <Form.Provider onFormChange={onFormChange}>{children}</Form.Provider>
      </DefectReportHistoryResetFiltersContext.Provider>
    </DefectReportHistoryFiltersContext.Provider>
  );
};

const useDefectReportHistoryFiltersFormName = () => FILTERS_FORM_NAME;

const useDefectReportHistoryFilters = () => {
  return useContext(DefectReportHistoryFiltersContext);
};

const useResetDefectReportHistoryFilters = () => {
  return useContext(DefectReportHistoryResetFiltersContext);
};

export {
  DefectRange,
  DefectReportHistoryDate,
  defaultFilters,
  DefectReportHistoryFiltersProvider,
  useDefectReportHistoryFiltersFormName,
  useDefectReportHistoryFilters,
  useResetDefectReportHistoryFilters,
};
